import React from 'react';
import { Container, Divider, Tab, Tabs } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';
import { useTranslation } from 'react-i18next';
import FarmLocations from '../../components/FarmLocations';
import { LocationsHierarchy, RoomFeedPanel } from '../../components';
import SilosTab from '../../components/Silos';
import useFeatures, { FeatureType } from '../../hooks/features';

type LocationsViewMode = 'list' | '2d';

const LocationsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasFeature } = useFeatures();

  const silosEnabled = hasFeature(FeatureType.VIEW_SILO);

  const resolveTab = (): string => {
    if (location.pathname.startsWith('/locations/silos') && silosEnabled) return 'silos';
    if (location.pathname.startsWith('/locations/feed')) return 'feed';
    return 'locations';
  };

  const currentTab = resolveTab();

  const [viewMode, setViewMode] = React.useState<LocationsViewMode>('list');

  const handleTabChange = (_event: React.ChangeEvent<any>, newValue: string) => {
    if (newValue === 'silos') {
      navigate('/locations/silos');
    } else if (newValue === 'feed') {
      navigate('/locations/feed');
    } else {
      navigate('/locations');
    }
  };

  return (
    <>
      <Container maxWidth={false} sx={{ maxWidth: 1280 }}>
        <Tabs value={currentTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
          <Tab value="locations" label={t('f-silo-tab-locations')} />
          <Tab value="feed" label={t('f-feed-reg-tab')} />
          {silosEnabled && <Tab value="silos" label={t('f-silo-tab-silos')} />}
        </Tabs>
        <Divider />
      </Container>

      {currentTab === 'locations' && (
        <Container maxWidth={false} sx={{ maxWidth: 1280 }}>
          <div className="mt-4 flex justify-end">
            <ToggleButtonGroup
              size="small"
              exclusive
              value={viewMode}
              onChange={(_e, value: LocationsViewMode | null) => value && setViewMode(value)}
            >
              <ToggleButton value="list">
                <ViewListIcon fontSize="small" className="mr-1" />
                {t('f-loc-hier-view-list')}
              </ToggleButton>
              <ToggleButton value="2d">
                <GridViewIcon fontSize="small" className="mr-1" />
                {t('f-loc-hier-view-2d')}
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
        </Container>
      )}

      <div className="mt-4">
        {currentTab === 'locations' && viewMode === 'list' && <FarmLocations mode="page" />}
        {currentTab === 'locations' && viewMode === '2d' && <LocationsHierarchy />}
        {currentTab === 'feed' && <RoomFeedPanel />}
        {currentTab === 'silos' && silosEnabled && <SilosTab />}
      </div>
    </>
  );
};

export default LocationsPage;
